import { Adapter } from '@mode-switch/adapt';
import { Field, Model, View } from '@mode-switch/model';
import { RecordsPlan, RecordsQuery } from '@mode-switch/query';
import { EMPTY_ENCODING, EncField, Encoding, VariableType } from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField, Switch } from '@mode/shared/util-switch';
import { batchEncoding, collectEncFields } from '../../chart-encoding-helpers';
import { makeField } from '../field';
import { makeFilters } from '../filter';
import { makeMarks } from '../mark';
import { makeTableSorts } from '../sort';

type ModeRole = FlamingoTypes.ModeRole;

export function buildRecordsPlan(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  limit: number,
  offset: number
): RecordsPlan<ModeRole> {
  return RecordsPlan.make(
    adapter,
    model,
    viewName,
    RecordsQuery.make({
      ...buildRecordsQuery(model, viewName, encoding, limit, offset),
    })
  );
}

export function buildRecordsPlanWithDetail(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  encDetail: EncField[] | undefined | null,
  limit: number,
  offset: number
): RecordsPlan<ModeRole> {
  let detail: Field<ModeRole>[] | undefined;
  if (encDetail?.length) {
    const detailMap = new Map<string, Field<ModeRole>>();
    detail = encDetail.map((encField) => {
      return makeField(model, viewName, EMPTY_ENCODING, detailMap, encField);
    });
  }

  return RecordsPlan.make(
    adapter,
    model,
    viewName,
    RecordsQuery.make({
      ...buildRecordsQuery(model, viewName, encoding, limit, offset),
      detail,
    })
  );
}

/** @internal */
export function buildRecordsQuery(
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  limit: number,
  offset: number
): RecordsQuery<ModeRole> {
  const view = Switch.Views.find(model, viewName);
  if (view == null) {
    throw Error(`No view with name ${viewName} was found`);
  }
  const fieldMap = new Map<string, Field<ModeRole>>();
  const naResult = swapNonScalarWithNA(view, encoding);

  encoding = naResult.encoding;
  model = Switch.Models.make(viewName, [view]);

  if (encoding.marks.channel === '*') {
    makeMarks(model, viewName, encoding, fieldMap, encoding.marks.properties);
  }

  const names = naResult.names;
  const fields = Array.from(fieldMap.values()).map((field): Field<ModeRole> => {
    const name = names.get(field.alias) ?? field.name;
    return {
      ...field,
      name,
    };
  });

  const sorts = makeTableSorts(model, viewName, encoding, fieldMap);
  const filters = makeFilters(model, viewName, encoding, fieldMap);

  return RecordsQuery.make({
    fields,
    sorts,
    filters,
    limit,
    offset,
  });
}

/**
 * Records queries do not support aggregate or analytic fields. This function
 * returns a new encoding with the following changes:
 *
 * - Changes the formula of aggregate/anaytic formulas to project the value "N/A"
 * - Omits Names/Values from names map
 *
 */
function swapNonScalarWithNA(view: View<ModeRole>, encoding: Encoding) {
  const names = new Map<string, string>();
  const batch = batchEncoding(encoding);
  const encFields = collectEncFields(encoding, '*');

  for (let encField of encFields) {
    const compiled = CompiledField.fromEncField(view, encField);
    const id = encField.id as string;

    // Names/Values shouldn't be here.. skip
    if (compiled.isSystem) {
      continue;
    }

    // Non-scalar formula
    if (compiled.isValid && !compiled.isAggregateable) {
      // Map the field id/alias to the original name
      names.set(id, compiled.displayName);

      // Have aggregate and analytic fields project "N/A"
      batch.updateField({
        id,
        formula: '"N/A"',
        variable: {
          type: VariableType.DISCRETE,
        },
      });
    }
  }

  return {
    encoding: batch.complete().encoding,
    names,
  };
}
